import { Card, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { type ExternalAccountDetailsAttributes } from "@src/appV2/redesign/Accounts/PayoutSettings/api/schema";
import { ExternalAccountLogo } from "@src/appV2/redesign/Accounts/PayoutSettings/components/ExternalAccountLogo";

import { CancellationType, type CancelledShiftDetailsData } from "../../api/cancelledShiftTypes";
import { ShiftInfoRow } from "../../ShiftDetails/components/ShiftInfoRow";
import { formatUsdFromCents } from "../../utils/formatters";

interface PayEligibilityCardProps {
  readonly data: CancelledShiftDetailsData;
  readonly payoutDestination: ExternalAccountDetailsAttributes | undefined;
}

export function PayEligibilityCard({ data, payoutDestination }: PayEligibilityCardProps) {
  const hasPaymentData =
    isDefined(data.paymentStatus) &&
    !data.paymentStatus.isUnknown &&
    isDefined(data.paymentStatus.payoutStatus);

  const isSentHome = data.cancellationType === CancellationType.SENT_HOME;
  const policyHours = isSentHome ? data.sentHomeChargeHours : data.lateCancellationFeeHours;
  const hasNoPolicy = !isDefined(policyHours) || policyHours === 0;

  const isCancelledOutsideWindow =
    !isSentHome &&
    data.lateCancellationPeriodHours > 0 &&
    data.leadTimeMinutes > data.lateCancellationPeriodHours * 60;

  const noPolicyMessage = getNoPolicyMessage({
    isSentHome,
    hasNoPolicy,
    isCancelledOutsideWindow,
    workplaceName: data.workplaceName,
    lateCancellationPeriodHours: data.lateCancellationPeriodHours,
  });

  if (!hasPaymentData && isDefined(noPolicyMessage)) {
    return (
      <Card variant="tertiary" sx={(theme) => ({ padding: theme.spacing(5) })}>
        <Stack spacing={3}>
          <Text semibold variant="body1">
            Payment
          </Text>
          <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
            {noPolicyMessage}
          </Text>
        </Stack>
      </Card>
    );
  }

  if (!hasPaymentData) {
    return null;
  }

  return (
    <Card variant="tertiary" sx={(theme) => ({ padding: theme.spacing(5) })}>
      <Stack spacing={3}>
        <Text semibold variant="body1">
          Payment
        </Text>
        <PayDetails paymentStatus={data.paymentStatus} payoutDestination={payoutDestination} />
      </Stack>
    </Card>
  );
}

interface PayDetailsProps {
  readonly paymentStatus: CancelledShiftDetailsData["paymentStatus"];
  readonly payoutDestination: ExternalAccountDetailsAttributes | undefined;
}

function PayDetails(props: PayDetailsProps) {
  const { paymentStatus, payoutDestination } = props;

  return (
    <Stack spacing={2}>
      {paymentStatus?.payoutStatus === "paid" && isDefined(paymentStatus.amountInCents) && (
        <ShiftInfoRow label="Amount paid" value={formatUsdFromCents(paymentStatus.amountInCents)} />
      )}
      {paymentStatus?.payoutStatus !== undefined && (
        <ShiftInfoRow
          label="Payment status"
          value={formatPaymentStatus(paymentStatus.payoutStatus)}
        />
      )}
      {isDefined(payoutDestination) && (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
            Payout destination
          </Text>
          <Stack direction="row" alignItems="center" spacing={2}>
            <ExternalAccountLogo externalAccount={payoutDestination} />
            <Text variant="body2">
              {payoutDestination.providerName ?? "Account"} ···· {payoutDestination.last4}
            </Text>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}

function formatPaymentStatus(status: string): string {
  switch (status) {
    case "paid": {
      return "Paid";
    }
    case "pending": {
      return "Pending";
    }
    case "failed": {
      return "Failed";
    }
    default: {
      return status;
    }
  }
}

interface NoPolicyMessageParams {
  readonly isSentHome: boolean;
  readonly hasNoPolicy: boolean;
  readonly isCancelledOutsideWindow: boolean;
  readonly workplaceName: string;
  readonly lateCancellationPeriodHours: number;
}

function getNoPolicyMessage(params: NoPolicyMessageParams): string | undefined {
  const {
    isSentHome,
    hasNoPolicy,
    isCancelledOutsideWindow,
    workplaceName,
    lateCancellationPeriodHours,
  } = params;

  if (hasNoPolicy) {
    return `${workplaceName} does not have a ${isSentHome ? "sent-home" : "cancellation"} pay policy.`;
  }

  if (isCancelledOutsideWindow) {
    return `This shift was cancelled outside the ${lateCancellationPeriodHours}h late cancellation window. No cancellation pay applies.`;
  }

  return undefined;
}
